/**
 * 画像編集v3 APIサービス
 * 一時画像アップロード、AI加工処理（モザイク、青空加工）のAPIと通信するサービス
 */
import axios from "axios";
import { OpenAPI } from "./core/OpenAPI";
import { getCsrfToken } from "../utils/csrf";

// ============ 一時画像アップロード関連 ============

export interface TempImageUploadResponse {
  success: boolean;
  image_url: string;
  temp_id: number;
  message: string;
}

export interface TempImageDeleteResponse {
  success: boolean;
  message: string;
}

// ============ AI加工処理関連 ============

export interface MosaicRequest {
  stub_id: string;
  mosaic_target_types: string[]; // ['face', 'licensePlate']
  input_image: string;
}

export interface BlueSkyRequest {
  stub_id: string;
  input_image: string;
}

export interface ImageEditingResponse {
  success: boolean;
  result_image_url?: string;
  message: string;
  usage_count?: number; // AI加工使用回数
  remaining_count?: number; // 残り使用可能回数
}

// ============ サービス実装 ============

export const ImageEditingService = {
  /**
   * 一時画像アップロード
   * Canvas APIで調整された画像をS3に一時保存し、URLとtemp_idを取得
   *
   * 処理フロー:
   * 1. フロントエンドでCanvas APIを使って画像を調整
   * 2. 調整後の画像をBlobに変換してこのAPIに送信
   * 3. S3に一時保存（TempImageEditingImageモデル使用）
   * 4. S3のURLとtemp_idを返す
   * 5. URLをモザイク/青空加工APIのinput_imageとして使用
   * 6. 処理完了後、deleteTempImageでtemp_idを使って削除
   *
   * @param imageBlob Canvas調整適用後の画像Blob
   * @returns 一時画像のURLとID
   */
  uploadTempImage: async (
    imageBlob: Blob,
  ): Promise<{ data: TempImageUploadResponse }> => {
    const formData = new FormData();
    const timestamp = Date.now();
    formData.append("image", imageBlob, `${timestamp}_adjusted_image.jpg`);

    try {
      const response = await axios.post<TempImageUploadResponse>(
        `${OpenAPI.BASE}/api/room-designs/image-editing/upload-temp/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": getCsrfToken(),
          },
          withCredentials: true,
        },
      );
      return response;
    } catch (error) {
      console.error("一時画像アップロードAPIエラー:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("エラー詳細:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });
        return error.response;
      }
      return {
        data: {
          success: false,
          image_url: "",
          temp_id: 0,
          message:
            "画像のアップロードに失敗しました。ネットワーク接続を確認してください。",
        },
      } as { data: TempImageUploadResponse };
    }
  },

  /**
   * 一時画像削除
   * AI処理完了後または処理キャンセル時に一時画像を削除
   *
   * フロントエンド実装例:
   * ```typescript
   * let tempId: number | null = null;
   * try {
   *   const uploadRes = await uploadTempImage(adjustedImageBlob);
   *   tempId = uploadRes.data.temp_id;
   *   await processBlueSky(uploadRes.data.image_url, stubId);
   * } finally {
   *   if (tempId) {
   *     await deleteTempImage(tempId);
   *   }
   * }
   * ```
   *
   * @param tempId 一時画像のID
   * @returns 削除結果
   */
  deleteTempImage: async (
    tempId: number,
  ): Promise<{ data: TempImageDeleteResponse }> => {
    try {
      const response = await axios.delete<TempImageDeleteResponse>(
        `${OpenAPI.BASE}/api/room-designs/image-editing/delete-temp/${tempId}/`,
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
          },
          withCredentials: true,
        },
      );
      return response;
    } catch (error) {
      console.error("一時画像削除APIエラー:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("エラー詳細:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });
        return error.response;
      }
      return {
        data: {
          success: false,
          message: "一時画像の削除に失敗しました",
        },
      } as { data: TempImageDeleteResponse };
    }
  },

  /**
   * モザイク処理API
   * 顔とナンバープレート両方を選択した場合、1回のAPIコールで両方を処理
   *
   * @param params リクエストパラメータ
   * @returns 処理結果（result_image_urlを含むJSONレスポンス）
   */
  processMosaic: async (
    params: MosaicRequest,
  ): Promise<{ data: ImageEditingResponse }> => {
    try {
      const response = await axios.post<ImageEditingResponse>(
        `${OpenAPI.BASE}/api/room-designs/image-editing/ai-process/mosaic/`,
        params,
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfToken(),
          },
          withCredentials: true,
          timeout: 600000, // 600秒（10分）タイムアウト
        },
      );
      return response;
    } catch (error) {
      console.error("モザイク処理APIエラー:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("エラー詳細:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });

        // タイムアウトエラー（504）の場合
        if (error.response.status === 504) {
          return {
            data: {
              success: false,
              message: "処理がタイムアウトしました。再度お試しください。",
            },
          } as { data: ImageEditingResponse };
        }

        return error.response;
      }

      // ネットワークエラー
      return {
        data: {
          success: false,
          message: "ネットワークエラーが発生しました。接続を確認してください。",
        },
      } as { data: ImageEditingResponse };
    }
  },

  /**
   * 青空加工API
   * 曇天や悪天候の空を青空に置き換える
   *
   * @param params リクエストパラメータ
   * @returns 処理結果（result_image_urlを含むJSONレスポンス）
   */
  processBlueSky: async (
    params: BlueSkyRequest,
  ): Promise<{ data: ImageEditingResponse }> => {
    try {
      const response = await axios.post<ImageEditingResponse>(
        `${OpenAPI.BASE}/api/room-designs/image-editing/ai-process/blue-sky/`,
        params,
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCsrfToken(),
          },
          withCredentials: true,
          timeout: 600000, // 600秒（10分）タイムアウト
        },
      );
      return response;
    } catch (error) {
      console.error("青空加工APIエラー:", error);
      if (axios.isAxiosError(error) && error.response) {
        console.error("エラー詳細:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });

        // タイムアウトエラー（504）の場合
        if (error.response.status === 504) {
          return {
            data: {
              success: false,
              message: "処理がタイムアウトしました。再度お試しください。",
            },
          } as { data: ImageEditingResponse };
        }

        return error.response;
      }

      // ネットワークエラー
      return {
        data: {
          success: false,
          message: "ネットワークエラーが発生しました。接続を確認してください。",
        },
      } as { data: ImageEditingResponse };
    }
  },
};
